import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { Sky, Environment, PerformanceMonitor } from "@react-three/drei";
import { Physics } from "@react-three/rapier";
import { useSearchParams } from "react-router-dom";
import { EcctrlJoystick } from "ecctrl";
import { useWallet } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";

import { ThroneHall, HALL_SPAWN } from "./ThroneHall";
import { ControlsEcctrl } from "./ControlsEcctrl";
import { loadSpawn } from "./playerSpawn";
import { EcctrlAvatar } from "./EcctrlAvatar";
import { PostFX } from "./PostFX";
import { useStore } from "../store";
import { joinLobby, type PubkeyAttester } from "../net/colyseus";
import { useIdentity } from "../identity";
import { MicHud } from "../voice/MicHud";
import { SkinPicker } from "../lobby/SkinPicker";
import { NameEditor } from "../lobby/NameEditor";
import { derivedSkinColor } from "../lobby/skinColor";
import { SpeakerList } from "../voice/SpeakerList";
import { PlayerVoicePanel } from "../voice/PlayerVoicePanel";
import { RoomMusicPlayer } from "../music/RoomMusicPlayer";
import { KothThroneStation } from "../games/last-touch/KothThroneStation";
import { KothHud } from "../games/last-touch/KothHud";
import { KothHudPanel } from "../games/last-touch/KothHudPanel";
import { useKothLobbyController } from "../games/last-touch/useKothLobbyController";
import { KingNameplate } from "../games/last-touch/KingNameplate";
import { PotBanner } from "../games/last-touch/PotBanner";
import { PotCauldron } from "../games/last-touch/PotCauldron";
import { TokenChartSign } from "../games/last-touch/TokenChartSign";
import { RoundTimerPlaque } from "../games/last-touch/RoundTimerPlaque";
import { RecentActivityPlaque } from "../games/last-touch/RecentActivityPlaque";
import { LeaderboardPlaque } from "../games/last-touch/LeaderboardPlaque";
import { AllTimeKingsPlaque } from "../games/last-touch/AllTimeKingsPlaque";
import { LastRoundPlaque } from "../games/last-touch/LastRoundPlaque";
import { ChatMirrorBoard } from "../games/last-touch/ChatMirrorBoard";
import { RulesBoard } from "../games/last-touch/RulesBoard";
import { RoadmapBoard } from "../games/last-touch/RoadmapBoard";
import { FlywheelBoard } from "../games/last-touch/FlywheelBoard";
import { FlywheelHelp } from "../games/last-touch/FlywheelModal";
import { RoundWinners } from "../games/last-touch/WinnersModal";
import { useIsTouch } from "../util/useIsTouch";
import { PointerLockBinder, PointerLockHint } from "./PointerLockBinder";
import { TabRefocusGuard, WebGLContextRecovery } from "./TabRefocusGuard";
import { AerialCamera } from "./AerialCamera";
import { AerialToggle } from "./AerialToggle";
import { BroadcastDirector } from "../watch/BroadcastDirector";
import { BroadcastHud } from "../watch/BroadcastHud";

interface LobbySceneProps {
  /**
   * Broadcast/streaming mode (the /watch route): spectator join, director-driven
   * camera, broadcast HUD — no player rig, no player-facing chrome. Defaults to
   * false so the normal lobby is byte-for-byte unchanged.
   */
  broadcast?: boolean;
}

/**
 * High-frequency remote-player rendering, deliberately split out of
 * <LobbyScene>. It subscribes to `remotePlayers` on its own, so the 30 Hz
 * stream of position patches only re-renders this small avatar list — not the
 * throne, the ~15 wall boards, or the HUD. <EcctrlAvatar> already interpolates
 * each body, so per-patch churn here is cheap.
 */
function RemoteAvatars(): JSX.Element {
  const remotePlayers = useStore((s) => s.remotePlayers);
  return (
    <>
      {Array.from(remotePlayers.values())
        .filter((p) => !p.isSpectator)
        .map((p) => (
          <EcctrlAvatar
            key={p.id}
            displayName={p.displayName}
            position={[p.x, p.y, p.z]}
            rotY={p.rotY}
            identity={p.id}
            colorHex={p.colorHex}
            colorIdentity={p.pubkey || p.id}
            isHolder={p.isHolder}
          />
        ))}
    </>
  );
}

/**
 * The throne + crowned king avatar. Resolves the reigning king from the player
 * set, but collapses the lookup to a stable string key so it only re-renders
 * when the king's identity/colour/name actually changes — a busy lobby full of
 * moving players no longer re-renders the throne on every position patch.
 */
function ThroneKingStation({
  koth,
}: {
  koth: ReturnType<typeof useKothLobbyController>;
}): JSX.Element {
  const r = koth.round;
  const kingPk =
    r && !r.currentKing.equals(PublicKey.default) ? r.currentKing.toBase58() : null;
  // Selector returns a primitive "colorHex\u0000displayName\u0000id" (or ""),
  // so zustand's default Object.is comparison re-renders only on a real change.
  const kingKey = useStore((s) => {
    if (!kingPk) return "";
    const king =
      s.localPlayer?.pubkey === kingPk
        ? s.localPlayer
        : Array.from(s.remotePlayers.values()).find((p) => p.pubkey === kingPk);
    return king ? `${king.colorHex}\u0000${king.displayName}\u0000${king.id}` : "";
  });
  const [kingColorHex, kingDisplayName, kingIdentity] = kingKey
    ? kingKey.split("\u0000")
    : [undefined, undefined, undefined];
  return (
    <KothThroneStation
      round={koth.round}
      localWalletPubkey={koth.localWalletPubkey}
      kingColorHex={kingColorHex}
      kingDisplayName={kingDisplayName}
      kingIdentity={kingIdentity}
    />
  );
}

export function LobbyScene({ broadcast = false }: LobbySceneProps = {}): JSX.Element {
  // NB: we deliberately do NOT subscribe to `remotePlayers` here. Player
  // position patches arrive at 30 Hz and cloning the map on each one would
  // re-render this whole component — the throne, all ~15 wall boards, the HUD —
  // several times per second per moving player, which is the bulk of the
  // main-thread jank that reads as "late / unresponsive". The high-frequency
  // bits live in <RemoteAvatars> and <ThroneKingStation>, which subscribe on
  // their own so only they re-render.
  // Broadcast mode follows the keeper's rolling open rounds; the normal lobby
  // keeps its ?round=/localStorage resolution.
  const koth = useKothLobbyController({ followOpenRound: broadcast });
  const isTouch = useIsTouch();

  // Adaptive resolution. We start at the device pixel ratio capped at 1.5, then
  // let <PerformanceMonitor> drop to 1.0 the instant the GPU can't keep up (and
  // ease back up when it can). This is what lets "dogshit on my machine"
  // self-correct instead of us hard-coding one dpr for every GPU.
  const maxDpr = useMemo(
    () => Math.min((typeof window !== "undefined" ? window.devicePixelRatio : 1) || 1, 1.5),
    [],
  );
  const [dpr, setDpr] = useState(maxDpr);

  // WebGL context-loss recovery (the "tab out → black screen" bug). On loss we
  // pause the render loop (`frameloop: never`) and unmount post-FX so nothing
  // renders against the freed context — otherwise the EffectComposer throws and
  // the ErrorBoundary blacks the page. On restore we resume + remount post-FX.
  // If the browser never restores (long AFK), `glEpoch` force-remounts the whole
  // <Canvas> for a fresh context. See <WebGLContextRecovery>.
  const [glLost, setGlLost] = useState(false);
  const [glEpoch, setGlEpoch] = useState(0);
  const onGlLost = useCallback(() => setGlLost(true), []);
  const onGlRestored = useCallback(() => setGlLost(false), []);
  const onGlUnrecovered = useCallback(() => {
    setGlLost(false);
    setGlEpoch((n) => n + 1);
  }, []);

  // Resolve the king player's appearance by matching `currentKing` to a
  // connected player's pubkey, so the throne renders an exact copy of their
  // avatar. Null when the king isn't in this room (left, or /watch spectator) —
  // the throne then falls back to the pubkey's deterministic color.
  const { wallet, guest } = useIdentity();
  const walletAdapter = useWallet();
  const [searchParams] = useSearchParams();

  // Build the wallet-sig attester for the lobby's pubkey:challenge handshake.
  // Stays undefined when no wallet is connected — guests still join, just
  // without a verified `player.pubkey` on the schema. Also undefined when the
  // player opted out via the connect modal's "Show your wallet address"
  // checkbox: they stay pseudonymous (still playable; touches are on-chain),
  // their address isn't broadcast, and holder perks don't apply. Flipping the
  // toggle changes this memo's identity, which re-runs the join effect below.
  const broadcastPubkey = useStore((s) => s.broadcastPubkey);
  const attester = useMemo<PubkeyAttester | undefined>(() => {
    if (!broadcastPubkey) return undefined;
    if (!wallet.pubkey || !walletAdapter.signMessage) return undefined;
    const signMessage = walletAdapter.signMessage;
    return {
      pubkey: wallet.pubkey,
      signMessage: (msg) => signMessage(msg),
    };
  }, [broadcastPubkey, wallet.pubkey, walletAdapter.signMessage]);
  // ?uikit=1 swaps the DOM KothHud for the in-canvas uikit panel. Off by
  // default so the working DOM HUD (mobile-tuned big tap target, top placement
  // that doesn't fight the joystick) stays the default until the uikit path
  // proves out in real playtests.
  const useUikitHud = searchParams.get("uikit") === "1" && !isTouch;
  // ?spectate=1 (or =true) joins as a listen-only spectator: no avatar, no
  // collision, free-cam over the throne hall, no mic publish, no game-action
  // buttons. Useful for streamers/ops watching a live round.
  const asSpectator =
    broadcast ||
    (() => {
      const v = searchParams.get("spectate");
      return v === "1" || v === "true";
    })();

  // Resume at the player's last position (persisted to localStorage each frame),
  // falling back to the hall entrance for a first visit / cleared storage. Read
  // once on mount and frozen — ecctrl treats a changing spawn array as a
  // teleport, so this must stay referentially stable for the session.
  const resumeSpawn = useMemo(() => loadSpawn(HALL_SPAWN), []);

  useEffect(() => {
    let cancelled = false;
    let joinedRoom: Awaited<ReturnType<typeof joinLobby>> | null = null;
    void (async () => {
      try {
        // Reuse the persisted guest identity (localStorage) so the player keeps
        // the same name across refreshes instead of being reassigned a fresh
        // random one every join.
        const room = await joinLobby({
          displayName: guest.displayName,
          avatarUrl: guest.avatarUrl,
          attester,
          asSpectator,
          // Always send a concrete, palette-valid color so it's broadcast to
          // every client and stays identical across reloads. An explicit pick
          // wins; otherwise hash a STABLE identity (wallet pubkey, else the
          // persistent guest id) — never the Colyseus sessionId, which is fresh
          // on each connection and is what made guest colors swap on reload.
          colorHex:
            useStore.getState().skinColorHex ??
            derivedSkinColor(wallet.pubkey || guest.id),
        });
        if (cancelled) {
          // StrictMode double-mount or unmount during join — drop this session
          // so it doesn't linger as a ghost remote player after the second mount.
          void room.leave();
          return;
        }
        joinedRoom = room;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[lobby] joinLobby failed (server offline?):", err);
      }
    })();
    return () => {
      cancelled = true;
      if (joinedRoom) void joinedRoom.leave();
    };
    // Re-join when the connected wallet pubkey changes so the server stores
    // it on the Player schema and the LiveKit identity is consistent.
    // Re-join on asSpectator change too — switching modes requires a new room
    // session so the server can rebuild the voice token with/without canPublish.
    // attester is in the dep list because a wallet (dis)connect changes it;
    // the join effect must rerun so the new room negotiates the right pubkey.
    // guest fields are stable (persisted) so they don't trigger extra rejoins.
  }, [wallet.pubkey, asSpectator, attester, guest.displayName, guest.avatarUrl]);

  // F-to-touch: claim the throne with the F key while standing in range (the
  // player controller sets store.nearThrone by distance to the throne). This is
  // the fast, pointer-lock-friendly path — no need to unlock the cursor to click
  // the HUD button mid-round. Gating mirrors the HUD touch button. kothRef keeps
  // the latest controller without re-binding the listener every render.
  const kothRef = useRef(koth);
  kothRef.current = koth;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.code !== "KeyF" || e.repeat) return; // one tx per deliberate press
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) {
        return; // don't fire while typing in chat / a form
      }
      if (!useStore.getState().nearThrone) return;
      const k = kothRef.current;
      if (!k.walletConnected || !k.round || k.expired || k.settled || k.txInFlight || k.isYouKing) {
        return;
      }
      void k.touch();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      {/* EcctrlJoystick MUST mount OUTSIDE <Canvas> as a sibling — it ships
          its own internal canvases wrapped in position:fixed divs (see
          ecctrl/src/EcctrlJoystick.tsx). buttonNumber={0} hides the right-hand
          action pad we don't use (TOUCH lives in <KothHud>). Single-finger
          camera drag is handled inside <Ecctrl> by useFollowCam — no extra
          wiring needed on the touch side. */}
      {isTouch && !broadcast && <EcctrlJoystick buttonNumber={0} />}

      <Canvas
        // Remount (fresh GL context) when the browser never restores after a
        // long AFK; pause the loop while the context is lost so post-FX can't
        // render against a freed target. See <WebGLContextRecovery>.
        key={glEpoch}
        frameloop={glLost ? "never" : "always"}
        shadows={!isTouch}
        camera={{ position: [0, 5, 18], fov: 55 }}
        gl={{
          // MSAA off: the EffectComposer renders the scene to its own target
          // with multisampling={0} (see PostFX), so geometry never touches the
          // multisampled default framebuffer — `antialias: true` was paying for
          // a sample buffer that does nothing here. Off is a free win.
          antialias: false,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.05,
          // Pin to the discrete GPU on dual-GPU laptops. The integrated GPU
          // both lags under the bloom/shadow stack and drops the WebGL context
          // far more readily on tab-out — the two symptoms players hit most.
          powerPreference: "high-performance",
        }}
        // Resolution is driven by <PerformanceMonitor> below, not a fixed range,
        // so it scales to the player's real GPU at runtime.
        dpr={dpr}
      >
        <PerformanceMonitor
          // Ease up to the device cap when there's headroom, halve resolution
          // the moment frames dip, and lock low if it keeps oscillating.
          onIncline={() => setDpr(maxDpr)}
          onDecline={() => setDpr(1)}
          flipflops={3}
          onFallback={() => setDpr(1)}
        />
        <TabRefocusGuard />
        <WebGLContextRecovery
          onLost={onGlLost}
          onRestored={onGlRestored}
          onUnrecovered={onGlUnrecovered}
        />
        <AerialCamera />
        {broadcast && <BroadcastDirector round={koth.round} />}
        <color attach="background" args={["#1a1208"]} />
        <fog attach="fog" args={["#1a1208", 10, 32]} />
        <Sky distance={450000} sunPosition={[10, 5, 10]} turbidity={6} />

        {/* Image-based lighting — drei loads a CC0 HDRI from its CDN. The
            'sunset' preset is warm, low-angle, and reads as late-afternoon
            light pouring through the gothic window slits. background={false}
            (default) means it lights the scene but doesn't replace <Sky>. */}
        <Environment preset="sunset" />

        {/* Low-intensity warm fill — HDRI carries the ambient; this is just
            a nudge so PBR materials in shadow don't crush to black. */}
        <ambientLight intensity={0.18} color="#ffd6a0" />

        {/* Sun through windows — single shadow caster, low angle, warm. */}
        <directionalLight
          position={[6, 12, 14]}
          intensity={0.55}
          color="#ffc890"
          castShadow={!isTouch}
          shadow-mapSize={[1024, 1024]}
        />

        <Physics timeStep={1 / 60}>
          <ThroneHall withPhysics />

          <RemoteAvatars />

          <ThroneKingStation koth={koth} />

          {/* In-canvas uikit HUD — only when ?uikit=1. Positioned in front of
              the throne, facing the player approaching from +z. */}
          {useUikitHud && (
            <KothHudPanel
              controller={koth}
              position={[0, 3.4, 1.6]}
              rotation={[0, Math.PI, 0]}
            />
          )}

          {/* Wall-diegetic plaques — every game-state surface lives in the room.
              Positions track the enlarged 28m × 34m × 9m hall. */}
          <KingNameplate
            round={koth.round}
            localWalletPubkey={koth.localWalletPubkey}
            position={[0, 8.4, -7.8]}
          />
          {/* Holographic pot readout projected in mid-air above the pot. No
              physical rig — the anchor IS the float point, so it sits at a
              readable height over the cauldron rather than up at the ceiling. */}
          <PotBanner round={koth.round} position={[0, 3.2, 3.8]} />
          <PotCauldron round={koth.round} position={[0, 0, 3.8]} />
          {/* Live $CROWN chart on a floating sign beside the pot, angled toward
              players approaching the throne from +z. Position/distanceFactor are
              tuned by eye — easy to nudge. */}
          <TokenChartSign position={[5, 3, 4.2]} rotation={[0, -0.4, 0]} distanceFactor={7 / 3} />
          <LeaderboardPlaque
            position={[-13.7, 4.0, -3]}
            rotation={[0, Math.PI / 2, 0]}
            roundId={koth.roundId ? Number(koth.roundId.toString()) : null}
          />
          <RoundTimerPlaque
            round={koth.round}
            position={[13.7, 4.0, -3]}
            rotation={[0, -Math.PI / 2, 0]}
          />
          <AllTimeKingsPlaque
            position={[-13.7, 4.0, 3]}
            rotation={[0, Math.PI / 2, 0]}
          />
          <LastRoundPlaque
            position={[13.7, 4.0, 3]}
            rotation={[0, -Math.PI / 2, 0]}
          />
          {/* Chat + rules sit at z=9, between the columns at z=6 and z=12 so
              the column shafts don't block the wall plaques. */}
          <ChatMirrorBoard
            position={[-13.7, 4.0, 9]}
            rotation={[0, Math.PI / 2, 0]}
          />
          <RulesBoard
            position={[13.7, 4.0, 9]}
            rotation={[0, -Math.PI / 2, 0]}
            round={koth.round}
          />
          {/* Roadmap board pairs with RulesBoard on the same right wall,
              further toward the spawn so players read it on entry. */}
          <RoadmapBoard
            position={[13.7, 4.0, 15]}
            rotation={[0, -Math.PI / 2, 0]}
          />
          {/* Flywheel board mirrors the roadmap on the left wall — same
              z=15 entry-side slot so the "why hold & play" pitch is one of
              the first walls players read on spawn. */}
          <FlywheelBoard
            position={[-13.7, 4.0, 15]}
            rotation={[0, Math.PI / 2, 0]}
          />
          {/* Live feed of real on-chain touches — honest social proof in place
              of a fake crowd. Sits at z=21 on the left wall, right beside the
              spawn (z=22) so players read "stuff is happening" on entry. */}
          <RecentActivityPlaque
            round={koth.round}
            position={[-13.7, 4.0, 21]}
            rotation={[0, Math.PI / 2, 0]}
          />

          {!broadcast && <ControlsEcctrl spawn={resumeSpawn} />}
          {!broadcast && !isTouch && <PointerLockBinder />}
        </Physics>

        {/* Post-processing — skipped on touch devices; the bloom and
            color-grade passes are the main cost. ACES tonemap on the gl
            prop above stays active on all devices. */}
        {/* Unmount post-FX while the context is lost — a freed EffectComposer
            render target is the source of the black-screen crash. Remounts
            fresh (new render targets) on restore. */}
        {!isTouch && !glLost && <PostFX />}
      </Canvas>

      {!broadcast && (
        <>
          <PointerLockHint />
          {!useUikitHud && <KothHud controller={koth} />}
          {!isTouch && <ThronePrompt controller={koth} />}
          <FlywheelHelp />
          <RoundWinners controller={koth} />
          <AerialToggle />

          <MicHud />
          <SkinPicker />
          <NameEditor />
          <SpeakerList />
          <PlayerVoicePanel />
        </>
      )}
      {broadcast && <BroadcastHud controller={koth} />}
      <RoomMusicPlayer />
    </div>
  );
}

/**
 * "[F] Claim the throne" prompt — shows only when the local player is standing
 * in range of the throne (store.nearThrone, set by the controller) AND a touch
 * is actually possible (wallet connected, round open, not expired/settled, and
 * you're not already king). Pure DOM overlay; the keydown handler in LobbyScene
 * does the work. Hidden on touch devices (the HUD button is their path).
 */
function ThronePrompt({
  controller,
}: {
  controller: ReturnType<typeof useKothLobbyController>;
}): JSX.Element {
  const near = useStore((s) => s.nearThrone);
  const { round, expired, settled, isYouKing, walletConnected, txInFlight } = controller;
  if (!near || !walletConnected || !round || expired || settled || isYouKing) return <></>;
  return (
    <div
      style={{
        position: "absolute",
        bottom: "24%",
        left: "50%",
        transform: "translateX(-50%)",
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 16px",
        borderRadius: 12,
        background: "rgba(10,10,16,0.8)",
        border: "1px solid rgba(255,216,107,0.55)",
        color: "#ffd86b",
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: 0.3,
        boxShadow: "0 2px 18px rgba(0,0,0,0.5)",
        userSelect: "none",
      }}
    >
      <kbd
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 24,
          height: 24,
          padding: "0 6px",
          borderRadius: 6,
          background: "#ffd86b",
          color: "#1a1606",
          fontWeight: 800,
          fontSize: 13,
          boxShadow: "0 2px 0 rgba(0,0,0,0.35)",
        }}
      >
        F
      </kbd>
      <span>{txInFlight ? "Claiming…" : "Claim the throne"}</span>
    </div>
  );
}

export default LobbyScene;
